import { useState, useEffect } from 'react'
import { useAuth } from '../context/auth'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Building2, MapPin, Truck, CreditCard, FileText, CheckCircle, ChevronRight, ChevronLeft, Loader2, Save, Plus, X } from 'lucide-react'

type Step = 'personal' | 'location' | 'vehicle' | 'banking' | 'review' | 'complete'

interface Truck {
  id: string
  truck_type: string
  truck_plate: string
}

interface TowerOnboardingData {
  business_name: string
  business_registration: string
  tax_pin: string
  business_description: string
  business_phone: string
  business_email: string
  business_address: string
  location_link: string
  location_city: string
  location_area: string
  location_county: string
  contact_person_name: string
  contact_person_phone: string
  contact_person_role: string
  trucks: Truck[]
  bank_name: string
  mpesa_number: string
}

export default function TowerOnboardingDashboard() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<Step>('personal')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const [data, setData] = useState<TowerOnboardingData>({
    business_name: '',
    business_registration: '',
    tax_pin: '',
    business_description: '',
    business_phone: profile?.phone || '',
    business_email: profile?.email || '',
    business_address: '',
    location_link: '',
    location_city: '',
    location_area: '',
    location_county: '',
    contact_person_name: '',
    contact_person_phone: '',
    contact_person_role: '',
    trucks: [],
    bank_name: '',
    mpesa_number: ''
  })

  const [extractedCoords, setExtractedCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [newTruck, setNewTruck] = useState({ truck_type: 'Flatbed', truck_plate: '' })

  // Save to localStorage on every change
  useEffect(() => {
    if (profile) {
      localStorage.setItem(`tower_onboarding_${profile.id}`, JSON.stringify(data))
    }
  }, [data, profile])

  // Load from localStorage on mount
  useEffect(() => {
    if (profile) {
      const saved = localStorage.getItem(`tower_onboarding_${profile.id}`)
      if (saved) {
        try {
          const savedData = JSON.parse(saved)
          setData(prev => ({ ...prev, ...savedData }))
        } catch (e) {
          console.error('Error loading from localStorage:', e)
        }
      }
    }
  }, [profile])

  const steps = [
    { id: 'personal', title: 'Business Info', icon: Building2 },
    { id: 'location', title: 'Location & Contact', icon: MapPin },
    { id: 'vehicle', title: 'Fleet Details', icon: Truck },
    { id: 'banking', title: 'Banking & Payment', icon: CreditCard },
    { id: 'review', title: 'Review & Submit', icon: FileText },
    { id: 'complete', title: 'Complete', icon: CheckCircle }
  ]

  const currentStepIndex = steps.findIndex(s => s.id === currentStep)

  const parseLocationFromLink = async (link: string) => {
    if (!link || !link.includes('maps')) return

    try {
      let coords = null
      const embedMatch = link.match(/!2d(-?\d+\.\d+)!3d(-?\d+\.\d+)/)
      if (embedMatch) {
        coords = { lng: parseFloat(embedMatch[1]), lat: parseFloat(embedMatch[2]) }
      }

      if (coords) {
        setExtractedCoords(coords)
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&zoom=18&addressdetails=1`
        )
        const result = await response.json()

        if (result.address) {
          const addr = result.address
          const city = addr.city || addr.town || addr.village || addr.county || ''
          const area = addr.suburb || addr.district || addr.neighbourhood || ''
          const county = addr.county || addr.state || ''

          setData(prev => ({
            ...prev,
            location_city: city,
            location_area: area,
            location_county: county
          }))
        }
      }
    } catch (error) {
      console.error('Error parsing location:', error)
    }
  }

  const saveProgress = async () => {
    if (!profile) return
    setSaving(true)
    try {
      const fieldUpdate: any = {
        business_name: data.business_name,
        business_registration: data.business_registration,
        tax_pin: data.tax_pin,
        business_address: data.business_address,
        business_phone: data.business_phone,
        business_email: data.business_email,
        location_link: data.location_link,
        location_city: data.location_city,
        location_area: data.location_area,
        location_county: data.location_county,
        truck_type: data.trucks.length > 0 ? data.trucks[0].truck_type : null,
        truck_plate: data.trucks.length > 0 ? data.trucks[0].truck_plate : null,
        vehicle_photos: data.trucks,
        bank_name: data.bank_name,
        mpesa_number: data.mpesa_number,
        updated_at: new Date().toISOString()
      }

      if (extractedCoords) {
        fieldUpdate.location_lat = extractedCoords.lat
        fieldUpdate.location_lng = extractedCoords.lng
      }

      await supabase.from('profiles').update(fieldUpdate).eq('id', profile.id)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 2000)
    } catch (error) {
      console.error('Error saving progress:', error)
    } finally {
      setSaving(false)
    }
  }

  const nextStep = async () => {
    await saveProgress()
    const currentIndex = steps.findIndex(s => s.id === currentStep)
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1].id as Step)
    }
  }

  const prevStep = async () => {
    await saveProgress()
    const currentIndex = steps.findIndex(s => s.id === currentStep)
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1].id as Step)
    }
  }

  const submitRegistration = async () => {
    if (!profile) return
    setLoading(true)
    try {
      console.log('Submitting trucks:', data.trucks)
      console.log('Number of trucks:', data.trucks.length)

      const updateData: any = {
        business_name: data.business_name,
        business_registration: data.business_registration,
        tax_pin: data.tax_pin,
        business_address: data.business_address,
        business_phone: data.business_phone,
        business_email: data.business_email,
        phone: data.business_phone, // Also save to main phone field
        location_link: data.location_link,
        location_city: data.location_city,
        location_area: data.location_area,
        location_county: data.location_county,
        truck_type: data.trucks.length > 0 ? data.trucks[0].truck_type : null,
        truck_plate: data.trucks.length > 0 ? data.trucks[0].truck_plate : null,
        vehicle_photos: data.trucks,
        bank_name: data.bank_name,
        mpesa_number: data.mpesa_number,
        onboarding_completed: true, // Mark onboarding as complete
        updated_at: new Date().toISOString()
      }

      if (extractedCoords) {
        updateData.location_lat = extractedCoords.lat
        updateData.location_lng = extractedCoords.lng
      }

      console.log('Update data being sent:', updateData)

      const { error } = await supabase.from('profiles').update(updateData).eq('id', profile.id)

      if (error) {
        console.error('Database update error:', error)
        alert('Error saving registration: ' + error.message)
        return
      }

      console.log('Registration saved successfully')
      localStorage.removeItem(`tower_onboarding_${profile.id}`)
      setCurrentStep('complete')
    } catch (error) {
      console.error('Registration error:', error)
      alert('Error during registration: ' + (error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          background: 'var(--bg)',
          padding: '1.5rem',
          borderBottom: '1px solid var(--border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Tower Registration</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {saveSuccess && (
                <div style={{ fontSize: '0.8rem', color: 'var(--green)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <CheckCircle size={14} />
                  Progress saved
                </div>
              )}
              <button
                onClick={saveProgress}
                disabled={saving}
                className="btn-outline"
                style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
              >
                {saving ? <Loader2 className="spin" size={14} /> : <Save size={14} />}
                Save Progress
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {steps.map((step, index) => (
              <div
                key={step.id}
                style={{
                  flex: 1,
                  height: '4px',
                  borderRadius: '2px',
                  background: index <= currentStepIndex ? 'var(--primary)' : 'var(--border)',
                  transition: 'all 0.3s'
                }}
              />
            ))}
          </div>

          {/* Progress Percentage */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '0.75rem',
            fontSize: '0.85rem',
            color: 'var(--muted)'
          }}>
            <span>Step {currentStepIndex + 1} of {steps.length}</span>
            <span style={{ fontWeight: 600, color: 'var(--primary)' }}>
              {Math.round(((currentStepIndex + 1) / steps.length) * 100)}% Complete
            </span>
          </div>

          {/* Step Indicators */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
            {steps.map((step, index) => {
              const Icon = step.icon
              return (
                <div
                  key={step.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.25rem',
                    fontSize: '0.7rem',
                    color: index <= currentStepIndex ? 'var(--primary)' : 'var(--muted)'
                  }}
                >
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: index <= currentStepIndex ? 'var(--primary)' : 'var(--bg)',
                    border: index <= currentStepIndex ? '2px solid var(--primary)' : '2px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {index < currentStepIndex ? <CheckCircle size={14} color="white" /> : <Icon size={14} color={index <= currentStepIndex ? 'white' : 'var(--muted)'} />}
                  </div>
                  <span style={{ fontWeight: index === currentStepIndex ? 600 : 400 }}>
                    {step.title}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '2rem' }}>
          <div style={{
            background: 'var(--amber-light)',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid var(--amber)',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <CheckCircle size={18} color="var(--amber)" />
            <div>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Auto-save enabled</div>
              <div style={{ color: 'var(--muted)' }}>
                Your progress is saved automatically. Switch tabs or close browser - your data is safe!
              </div>
            </div>
          </div>

          {currentStep === 'personal' && (
            <>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.5rem' }}>Basic Business Information</h3>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Business Name *
                </label>
                <input
                  className="form-input"
                  type="text"
                  value={data.business_name}
                  onChange={(e) => setData(prev => ({ ...prev, business_name: e.target.value }))}
                  placeholder="e.g., Nairobi Towing Services Ltd"
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    Business Registration *
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    value={data.business_registration}
                    onChange={(e) => setData(prev => ({ ...prev, business_registration: e.target.value.toUpperCase() }))}
                    placeholder="e.g., BN/2024/123456"
                    style={{ width: '100%', textTransform: 'uppercase' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    Tax PIN *
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    value={data.tax_pin}
                    onChange={(e) => setData(prev => ({ ...prev, tax_pin: e.target.value.toUpperCase() }))}
                    placeholder="e.g., A005678901H"
                    style={{ width: '100%', textTransform: 'uppercase' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Business Description
                </label>
                <textarea
                  className="form-input"
                  value={data.business_description}
                  onChange={(e) => setData(prev => ({ ...prev, business_description: e.target.value }))}
                  placeholder="Briefly describe your business operations..."
                  style={{ width: '100%', minHeight: '80px', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    Business Phone *
                  </label>
                  <input
                    className="form-input"
                    type="tel"
                    value={data.business_phone}
                    onChange={(e) => setData(prev => ({ ...prev, business_phone: e.target.value }))}
                    placeholder="+254..."
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    Business Email *
                  </label>
                  <input
                    className="form-input"
                    type="email"
                    value={data.business_email}
                    onChange={(e) => setData(prev => ({ ...prev, business_email: e.target.value }))}
                    placeholder="business@company.com"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className="btn-primary" onClick={nextStep} style={{ flex: 1 }}>
                  Next <ChevronRight size={16} style={{ display: 'inline', marginLeft: '0.25rem' }} />
                </button>
              </div>
            </>
          )}

          {currentStep === 'location' && (
            <>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.5rem' }}>Location & Contact</h3>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Business Address *
                </label>
                <input
                  className="form-input"
                  type="text"
                  value={data.business_address}
                  onChange={(e) => setData(prev => ({ ...prev, business_address: e.target.value }))}
                  placeholder="Your business physical address"
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ background: 'var(--bg)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <MapPin size={18} color="var(--primary)" />
                  Operating Location
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    Google Maps Link *
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    value={data.location_link}
                    onChange={(e) => setData(prev => ({ ...prev, location_link: e.target.value }))}
                    onBlur={(e) => {
                      let url = e.target.value
                      const iframeMatch = url.match(/src="([^"]+)"/)
                      if (iframeMatch) {
                        url = iframeMatch[1]
                        setData(prev => ({ ...prev, location_link: url }))
                      }
                      parseLocationFromLink(url)
                    }}
                    placeholder="Paste Google Maps link or iframe"
                    style={{ width: '100%' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      City *
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      value={data.location_city}
                      onChange={(e) => setData(prev => ({ ...prev, location_city: e.target.value }))}
                      placeholder="e.g., Nairobi"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Area *
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      value={data.location_area}
                      onChange={(e) => setData(prev => ({ ...prev, location_area: e.target.value }))}
                      placeholder="e.g., Westlands"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      County *
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      value={data.location_county}
                      onChange={(e) => setData(prev => ({ ...prev, location_county: e.target.value }))}
                      placeholder="e.g., Nairobi"
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ background: 'var(--bg)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>Contact Person</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Name *
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      value={data.contact_person_name}
                      onChange={(e) => setData(prev => ({ ...prev, contact_person_name: e.target.value }))}
                      placeholder="Full name"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Role *
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      value={data.contact_person_role}
                      onChange={(e) => setData(prev => ({ ...prev, contact_person_role: e.target.value }))}
                      placeholder="e.g., Manager"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Phone *
                    </label>
                    <input
                      className="form-input"
                      type="tel"
                      value={data.contact_person_phone}
                      onChange={(e) => setData(prev => ({ ...prev, contact_person_phone: e.target.value }))}
                      placeholder="Phone number"
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className="btn-outline" onClick={prevStep} style={{ flex: 1 }}>
                  <ChevronLeft size={16} style={{ display: 'inline', marginRight: '0.25rem' }} /> Back
                </button>
                <button className="btn-primary" onClick={nextStep} style={{ flex: 1 }}>
                  Next <ChevronRight size={16} style={{ display: 'inline', marginLeft: '0.25rem' }} />
                </button>
              </div>
            </>
          )}

          {currentStep === 'vehicle' && (
            <>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.5rem' }}>Fleet Details</h3>

              {/* Add Truck Form */}
              <div style={{ background: 'var(--bg)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
                <h4 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Truck size={18} />
                  Add New Truck
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Truck Type *
                    </label>
                    <select
                      className="form-input"
                      value={newTruck.truck_type}
                      onChange={(e) => setNewTruck(prev => ({ ...prev, truck_type: e.target.value }))}
                      style={{ width: '100%' }}
                    >
                      <option value="Flatbed">Flatbed</option>
                      <option value="Wheel Lift">Wheel Lift</option>
                      <option value="Integrated">Integrated</option>
                      <option value="Hook and Chain">Hook and Chain</option>
                      <option value="Rotator">Rotator</option>
                      <option value="Heavy Duty">Heavy Duty</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Truck Plate *
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      value={newTruck.truck_plate}
                      onChange={(e) => setNewTruck(prev => ({ ...prev, truck_plate: e.target.value.toUpperCase() }))}
                      placeholder="e.g., KAB 123A"
                      style={{ width: '100%', textTransform: 'uppercase' }}
                    />
                  </div>
                </div>
                <button
                  className="btn-primary"
                  onClick={() => {
                    if (newTruck.truck_plate) {
                      setData(prev => ({
                        ...prev,
                        trucks: [...prev.trucks, { ...newTruck, id: crypto.randomUUID() }]
                      }))
                      setNewTruck({ truck_type: 'Flatbed', truck_plate: '' })
                    }
                  }}
                  disabled={!newTruck.truck_plate}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                >
                  <Plus size={16} />
                  Add Truck
                </button>
              </div>

              {/* Trucks List */}
              {data.trucks.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ marginBottom: '1rem' }}>Your Fleet ({data.trucks.length} truck{data.trucks.length !== 1 ? 's' : ''})</h4>
                  {data.trucks.map((truck, index) => (
                    <div
                      key={truck.id}
                      style={{
                        background: 'white',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '0.75rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{truck.truck_type}</div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>{truck.truck_plate}</div>
                      </div>
                      <button
                        onClick={() => {
                          setData(prev => ({
                            ...prev,
                            trucks: prev.trucks.filter((_, i) => i !== index)
                          }))
                        }}
                        style={{
                          background: 'var(--red-light)',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.5rem',
                          cursor: 'pointer',
                          color: 'var(--red)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <X size={16} />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {data.trucks.length === 0 && (
                <div style={{
                  background: 'var(--amber-light)',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                  border: '1px solid var(--amber)',
                  fontSize: '0.85rem',
                  color: 'var(--amber)',
                  textAlign: 'center'
                }}>
                  Add at least one truck to continue
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className="btn-outline" onClick={prevStep} style={{ flex: 1 }}>
                  <ChevronLeft size={16} style={{ display: 'inline', marginRight: '0.25rem' }} /> Back
                </button>
                <button
                  className="btn-primary"
                  onClick={nextStep}
                  disabled={data.trucks.length === 0}
                  style={{ flex: 1 }}
                >
                  Next <ChevronRight size={16} style={{ display: 'inline', marginLeft: '0.25rem' }} />
                </button>
              </div>
            </>
          )}

          {currentStep === 'banking' && (
            <>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.5rem' }}>Banking & Payment</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    Bank Name
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    value={data.bank_name}
                    onChange={(e) => setData(prev => ({ ...prev, bank_name: e.target.value }))}
                    placeholder="e.g., Equity Bank"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    M-Pesa Number *
                  </label>
                  <input
                    className="form-input"
                    type="tel"
                    value={data.mpesa_number}
                    onChange={(e) => setData(prev => ({ ...prev, mpesa_number: e.target.value }))}
                    placeholder="e.g., 0712345678"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className="btn-outline" onClick={prevStep} style={{ flex: 1 }}>
                  <ChevronLeft size={16} style={{ display: 'inline', marginRight: '0.25rem' }} /> Back
                </button>
                <button className="btn-primary" onClick={nextStep} style={{ flex: 1 }}>
                  Next <ChevronRight size={16} style={{ display: 'inline', marginLeft: '0.25rem' }} />
                </button>
              </div>
            </>
          )}

          {currentStep === 'review' && (
            <>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.5rem' }}>Review & Submit</h3>

              <div style={{ background: 'var(--bg)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '1rem' }}>Business Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.9rem' }}>
                  <div><strong>Name:</strong> {data.business_name}</div>
                  <div><strong>Registration:</strong> {data.business_registration}</div>
                  <div><strong>Tax PIN:</strong> {data.tax_pin}</div>
                  <div><strong>Phone:</strong> {data.business_phone}</div>
                  <div><strong>Email:</strong> {data.business_email}</div>
                  <div><strong>Address:</strong> {data.business_address}</div>
                </div>
              </div>

              <div style={{ background: 'var(--bg)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '1rem' }}>Location</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', fontSize: '0.9rem' }}>
                  <div><strong>City:</strong> {data.location_city}</div>
                  <div><strong>Area:</strong> {data.location_area}</div>
                  <div><strong>County:</strong> {data.location_county}</div>
                </div>
              </div>

              <div style={{ background: 'var(--bg)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '1rem' }}>Fleet ({data.trucks.length} truck{data.trucks.length !== 1 ? 's' : ''})</h4>
                {data.trucks.map((truck, index) => (
                  <div
                    key={truck.id}
                    style={{
                      background: 'white',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      padding: '0.75rem',
                      marginBottom: '0.5rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.9rem'
                    }}
                  >
                    <span><strong>{truck.truck_type}</strong></span>
                    <span>{truck.truck_plate}</span>
                  </div>
                ))}
              </div>

              <div style={{ background: 'var(--bg)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '1rem' }}>Payment</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.9rem' }}>
                  <div><strong>Bank:</strong> {data.bank_name}</div>
                  <div><strong>M-Pesa:</strong> {data.mpesa_number}</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className="btn-outline" onClick={prevStep} style={{ flex: 1 }}>
                  <ChevronLeft size={16} style={{ display: 'inline', marginRight: '0.25rem' }} /> Back
                </button>
                <button
                  className="btn-primary"
                  onClick={submitRegistration}
                  disabled={loading}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
                >
                  {loading ? <Loader2 className="spin" size={16} /> : 'Submit Registration'}
                </button>
              </div>
            </>
          )}

          {currentStep === 'complete' && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <CheckCircle size={40} color="white" />
              </div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>Registration Submitted!</h3>
              <p style={{ color: 'var(--muted)', lineHeight: 1.6, marginBottom: '1rem' }}>
                Your tower registration has been submitted for verification. Our team will review your information within 2-3 business days.
              </p>

              <div style={{
                background: 'var(--amber-light)',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                border: '1px solid var(--amber)',
                textAlign: 'left',
                fontSize: '0.9rem'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--amber)' }}>What happens next:</div>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', lineHeight: 1.8, color: 'var(--text)' }}>
                  <li>Our team will verify your business documents</li>
                  <li>You will receive a notification once approved</li>
                  <li><strong>Until approved:</strong> You can access your dashboard but cannot accept towing jobs</li>
                  <li><strong>After approval:</strong> You can start receiving and accepting towing requests</li>
                </ul>
              </div>

              <button
                className="btn-primary"
                onClick={() => navigate('/')}
                style={{ marginTop: '1rem' }}
              >
                Go to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
